import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { mockPrograms } from '../data/mentorshipData';
import { mockProjects } from '../data/mockData';
import CreateProgramModal from '../components/programs/CreateProgramModal';

const Programs: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      case 'upcoming':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'completed':
        return 'Completado';
      case 'upcoming':
        return 'Próximo';
      default:
        return status;
    }
  };

  const handleCreateProgram = (program: any) => {
    console.log('Nuevo programa creado:', program);
    setSnackbar({ 
      open: true, 
      message: `Programa "${program.name}" creado exitosamente` 
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Programas de Aceleración</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Nuevo Programa
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {mockPrograms.map(program => (
          <Card key={program.id}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {program.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {program.description}
                  </Typography>
                </Box>
                <Chip 
                  label={getStatusLabel(program.status)} 
                  color={getStatusColor(program.status) as any}
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 3, mt: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tipo
                  </Typography>
                  <Typography variant="body2">
                    {program.type}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Inicio
                  </Typography>
                  <Typography variant="body2">
                    {new Date(program.startDate).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Fin
                  </Typography>
                  <Typography variant="body2">
                    {new Date(program.endDate).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cohortes
                  </Typography>
                  <Typography variant="body2">
                    {program.cohortIds.length}
                  </Typography>
                </Box>
              </Box>

              {program.cohortIds.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Proyectos Participantes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Proyecto</TableCell>
                          <TableCell>Industria</TableCell>
                          <TableCell>Etapa</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mockProjects.slice(0, 2).map(project => (
                          <TableRow key={project.id}>
                            <TableCell>{project.name}</TableCell>
                            <TableCell>{project.industry}</TableCell>
                            <TableCell>
                              <Chip label={project.stage} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {mockPrograms.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay programas creados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Crea el primer programa de aceleración
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
          >
            Crear Primer Programa
          </Button>
        </Box>
      )}

      <CreateProgramModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleCreateProgram}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Programs;
